import {reportWidgetLoadStart} from '@wix/cashier-payments-widget/lazy';
import assert from 'assert';
import {map} from 'rxjs/operators';
import {ActionType, getType} from 'typesafe-actions';
import {assign, DoneInvokeEvent, ErrorExecutionEvent, GuardMeta} from 'xstate';
import {ActionMeta} from 'xstate/lib/types';
import {IDownloadLinks} from '../../../typings/general';
import {container} from '../../ioc/container';
import {services} from '../../ioc/services';
import {ENTITY_GET_REQUIREMENT} from '../../services/albumProvider/albumProviderConstant';
import {
  entityTypeSelector,
  getRequirementSelector,
  isSubscriptionRequiredSelector,
} from '../../services/albumProvider/albumProviderSelector';
import {BI_ERRORS} from '../../services/biErrorLogger/biErrors';
import {BI_SCHEMA} from '../../services/biLogger/biEvents';
import {BiLogger} from '../../services/biLogger/biLogger';
import {InitialTransactionStatus} from '../../services/cashier/getTransactionInfo';
import {closePage} from '../../services/closePage';
import {createPaymentsSnapshot} from '../../services/dgs/createPaymentsSnapshot';
import {fetchDownloadLinks} from '../../services/dgs/fetchDownloadLinks';
import {getDownloadLinksBySnapshotIdObserver} from '../../services/dgs/getDownloadLinksBySnapshotId';
import {subscribeDownload} from '../../services/dgs/subscribeDownload';
import {errorLogger} from '../../services/errorLogger/errorLogger';
import {IMediaPlatformApi} from '../../services/injectable/MediaPlatformApi/IMediaPlatformApi';
import {CheckStatusResult, FileStatus} from '../../services/injectable/MediaPlatformApi/types';
import {ILocationService} from '../../services/LocationService/ILocationService';
import {getEntityFileId} from '../../services/utils/getEntityFileId';
import {getDebugInfo} from '../../services/utils/withDebugInfo/withDebugInfo';
import {IWixPubSub} from '../../services/WixPubSub/IWixPubSub';
import {downloadLinksSuccessAction, sendBiErrorAction} from '../../store/actions';
import {
  CashierMandatoryFieldsAction,
  CashierWidgetLoadStateAction,
  DownloadLinksSuccessAction,
  EmailGotAction,
  OnNameYourPriceEnteredAction,
  PayWithCashierFailureAction,
  SendBiErrorAction,
} from '../../store/actions/RootAction';
import {
  dgsParamsSelector,
  emailSelector,
  instanceSelector,
  isExperimentEnabled,
  isIosSelector,
  redirectParamsSelector,
  snapshotIdSelector,
} from '../../store/selectors';
import {priceOverrideSelector} from '../../store/selectors/price.selector';
import {playerMachine} from '../playerMachine/playerMachine';
import {TrackChangedAction} from '../playerMachine/playerMachineActions';
import {AppContext} from './AppContext';
import {ExperimentNames} from '../../services/experiments/ExperimentNames';

export const machineOptions = ({biLogger}: {biLogger: BiLogger}) => ({
  actions: {
    updateOrigin: (context, event, meta) => {
      const wixPubSub = container.get<IWixPubSub>(services.wixPubSub);
      wixPubSub.publish('get-window.state-update.change-origin', {origin: meta.action.origin});
    },
    storeEmail: assign<AppContext, EmailGotAction>({email: (context, action) => action.payload.email}),
    storeSnapshotId: assign<AppContext, DoneInvokeEvent<{id: string}>>({
      snapshotId: (context, event) => event.data.id,
    }),
    assignEmptyDownloadLink: () => assign({downloadLinks: () => ({})}),
    storeDownloadLink: assign<AppContext, DownloadLinksSuccessAction | DoneInvokeEvent<IDownloadLinks>>({
      downloadLinks: (context, event) => {
        return 'payload' in event ? event.payload : event.data;
      },
    }),
    updateCashierWidgetStatus: assign<AppContext, CashierWidgetLoadStateAction>({
      cashierWidgetLoading: (context, {payload}) => context.cashierWidgetLoading && payload,
    }),
    storeMandatoryFields: assign<AppContext, CashierMandatoryFieldsAction>({
      mandatoryFields: (context, {payload}) => payload,
    }),
    storePriceOverride: assign<AppContext, OnNameYourPriceEnteredAction>({
      priceOverride: (context, event) => event.payload.price,
    }),
    storeError: assign<AppContext, ErrorExecutionEvent>({error: (context, event) => event.data}),
    logError: (context, event) => {
      const reason = event.data;
      const type = event.type;
      errorLogger.logIfNotSeen({bi: BI_ERRORS.getWindow.unhandled.generic}, {reason, type});
      console.error(reason);
    },
    [getType(sendBiErrorAction)]: (
      context,
      event: ErrorExecutionEvent,
      meta: ActionMeta<AppContext, ActionType<typeof sendBiErrorAction>>,
    ) => {
      const action = meta.action as SendBiErrorAction;
      const error = action.payload;
      errorLogger.logError({bi: error}, {reason: event.data});
    },
    reportWidgetLoadStart: () => reportWidgetLoadStart(),
    storePaymentErrorMessage: assign<AppContext, PayWithCashierFailureAction>({
      paymentErrorMessage: (context, event) => event.payload.errorMessage,
    }),
    paymentCompletedAnalytics: (context, event, meta) => {
      const paymentStatus = meta.action.paymentStatus;
      assert(paymentStatus, 'No payment status in meta');
      biLogger.log(BI_SCHEMA.player.downloadPage.paymentCompleted, {
        paymentStatus,
        type: entityTypeSelector(context),
        errorMessage: (event.payload && event.payload.errorMessage) || '',
      });
    },
    closeModal: (context, action) => {
      closePage(biLogger, action.payload || 'history');
    },
    emailGotAnalytics: (context, event, meta) => {
      const isSuccess = meta.action.isSuccess;
      assert(typeof isSuccess === 'boolean', 'No isSuccess in meta');
      biLogger.log(BI_SCHEMA.player.downloadPage.emailEntered, {
        isSuccess,
        type: entityTypeSelector(context),
      });
    },
    backActionAnalytics: (context) => {
      biLogger.log(BI_SCHEMA.player.downloadPage.back.clicked, {
        type: entityTypeSelector(context),
      });
    },
    subscribeAnalytics: (context, event, meta) => {
      biLogger.log(BI_SCHEMA.player.downloadPage.enterEmail.validation.Complete, {
        type: entityTypeSelector(context),
        isSuccess: meta.action.isSuccess,
        errorMsg: event.data && event.data.message ? event.data.message : '',
      });
    },
    retryPaymentAnalytics: (context: AppContext) => {
      biLogger.log(BI_SCHEMA.player.downloadPage.retryPaymentClicked, {
        type: entityTypeSelector(context),
      });
    },
    subscribeEmailGotAnalytics: (context) => {
      biLogger.log(BI_SCHEMA.player.downloadPage.subscribeToDownload.clicked, {
        type: entityTypeSelector(context),
      });
    },
    logNotReadyAlbum: (context: AppContext, event: DoneInvokeEvent<CheckStatusResult>) => {
      const debugInfo = getDebugInfo(event.data);

      console.error(
        `Cannot download entity because it's in ${event.data.status}` + debugInfo?.originalStatus
          ? ` OriginalStatus: ${debugInfo?.originalStatus}`
          : '',
      );
      errorLogger.logError(
        {
          bi: BI_ERRORS.getWindow.album.notReady,
          biParams: [context.album.id, event.data.status, debugInfo?.originalStatus],
        },
        {status: event.data.status, response: debugInfo?.response},
      );
    },
    updateTrack: assign<AppContext, TrackChangedAction>({
      track: (context, action) => {
        return action.payload.track;
      },
    }),
    clearTrack: assign<AppContext>({track: undefined}),
  },
  guards: {
    isExperimentEnabled: (context, action, meta: GuardMeta<AppContext, any>) => {
      const cond = meta.cond as Record<string, ExperimentNames>;
      return isExperimentEnabled(cond.experimentName)(context);
    },
    isTrackInfoRoute: () => {
      const locationService = container.get<ILocationService>(services.location);
      return locationService.getHash().startsWith('#/track-info');
    },
    isInitialTransactionCompletedWithSnapshot: (context: AppContext) =>
      (context.initialTransactionStatus === InitialTransactionStatus.PENDING ||
        context.initialTransactionStatus === InitialTransactionStatus.SUCCESS) &&
      context.snapshotId !== undefined,
    isInitialTransactionPending: (context: AppContext) =>
      context.initialTransactionStatus === InitialTransactionStatus.PENDING && context.snapshotId === undefined,
    isInitialTransactionSuccess: (context: AppContext) =>
      context.initialTransactionStatus === InitialTransactionStatus.SUCCESS && context.snapshotId === undefined,
    isInitialTransactionFailure: (context: AppContext) =>
      context.initialTransactionStatus === InitialTransactionStatus.ERROR,
    isDemoFlow: (context: AppContext) => context.album.demo === true,
    isBuyFlow: (context) => getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.BUY,
    isFreeFlow: (context) => getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.FREE,
    isNameYourPriceFlow: (context) => getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.NAME_YOUR_PRICE,
    isSubscribeFlow: (context) => getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.SUBSCRIBE,
    isNoneFlow: (context) => getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.NONE,
    isZeroPriceOverride: (context, event) => event.payload.price === 0,
    isSubscriptionRequired: (context) => isSubscriptionRequiredSelector(context),
    isIos: isIosSelector,
    canDownloadAlbum: (context, event: DoneInvokeEvent<CheckStatusResult | undefined>) => {
      return !event.data || event.data.status === FileStatus.Ready;
    },
  },
  services: {
    playerMachine,
    getSnapshot: (context, event) => {
      const params = {
        ...dgsParamsSelector(context),
        priceOverride: priceOverrideSelector(context),
        email: emailSelector(context),
        redirectParams: redirectParamsSelector(context),
      };

      return createPaymentsSnapshot(params);
    },
    getFreeDownloadLinks: (context) => {
      return fetchDownloadLinks(dgsParamsSelector(context));
    },
    getDownloadLink: (context, event) => {
      const snapshotId = snapshotIdSelector(context);
      const instance = instanceSelector(context);
      return getDownloadLinksBySnapshotIdObserver({
        snapshotId,
        instance,
      }).pipe(map(downloadLinksSuccessAction));
    },
    subscribeByEmail: (context) => {
      const params = {
        ...dgsParamsSelector(context),
        email: emailSelector(context),
      };
      return subscribeDownload(params);
    },
    checkAlbumDownloadStatus: async (context: AppContext): Promise<CheckStatusResult | undefined> => {
      if (getRequirementSelector(context) === ENTITY_GET_REQUIREMENT.NONE) {
        return;
      }
      const mediaPlatformApi = container.get<IMediaPlatformApi>(services.mediaPlatformApi);

      const fileId = getEntityFileId(context);
      return mediaPlatformApi.getFileStatus(fileId);
    },
  },
});
